"use client";

import Image from "next/image";
import { Heading } from "@/components/ui/typography";
import Link from "next/link";

export default function OffshoreCTA({ heading = "Why Bookkeeping Protects Your Offshore Tax Position" }: { heading?: string } = {}) {
  const items = [
    "transaction flows",
    "customer geography",
    "management decisions",
    "operational structure evidence",
  ];

  return (
    <section className="md:py-[60px] py-[30px] px-4 lg:px-8">
      <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row gap-8 items-center">
        {/* LEFT IMAGE */}
        <div className="flex-1 w-full">
          <div className="relative w-full h-[260px] sm:h-[320px] md:h-[420px] lg:h-[480px] rounded-2xl overflow-hidden">
            <Image
              src="/offshore-frame.webp"
              alt="Offshore Illustration"
              fill
              className="object-cover"
            />
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="flex-1 flex flex-col gap-8">
          {/* Heading */}
          <div className="flex flex-col gap-6">
            <Heading as="h2" variant="section">
              {heading}
            </Heading>

            <p className="text-white/70 text-[16px] leading-[24px] font-medium">
              Many founders don&apos;t realize this. Your Hong Kong offshore
              profits exemption depends on documentation quality. Accurate
              bookkeeping supports your offshore position during review.
            </p>
          </div>

          {/* Checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {items.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.64921 10.4499L9.49921 13.2999L15.1992 7.59994M10.4492 19.9499C11.697 19.9515 12.9328 19.7065 14.0856 19.229C15.2384 18.7515 16.2855 18.0509 17.1666 17.1674C18.0501 16.2862 18.7508 15.2391 19.2283 14.0863C19.7058 12.9335 19.9508 11.6977 19.9492 10.4499C19.9508 9.20215 19.7058 7.96635 19.2282 6.81355C18.7507 5.66075 18.0501 4.61368 17.1666 3.7325C16.2855 2.84902 15.2384 2.14838 14.0856 1.67086C12.9328 1.19334 11.697 0.948342 10.4492 0.949959C9.20142 0.948368 7.96562 1.19337 6.81282 1.67089C5.66002 2.14842 4.61294 2.84904 3.73177 3.7325C2.84831 4.61368 2.14768 5.66075 1.67016 6.81355C1.19264 7.96635 0.947636 9.20215 0.949227 10.4499C0.94761 11.6977 1.1926 12.9335 1.67013 14.0863C2.14765 15.2391 2.84829 16.2862 3.73177 17.1674C4.61294 18.0508 5.66002 18.7515 6.81282 19.229C7.96562 19.7065 9.20142 19.9515 10.4492 19.9499Z"
                    stroke="#155DFC"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <p className="text-white/70 text-[14px] md:text-[16px] leading-[150%]">
                  {item}
                </p>
              </div>
            ))}
          </div>

          {/* Supporting Text */}
          <p className="text-white text-[16px] leading-[24px] font-medium">
            This is why compliance is part of your tax strategy, not just
            administration.
          </p>

          {/* Button */}
          <a
            href="https://calendly.com/d/cxy2-dy2-tqk/mutetaxes-consultation-call"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg w-fit"
          >
            <span className="text-[14px] font-medium">
              Book a free consultation
            </span>
            <Image src="/button-arrow.svg" alt="arrow" width={18} height={18} />
          </a>

          {/* Link */}
          <Link
            href="/tax-optimization"
            className="text-[#335cff] text-[14px] md:text-[16px] underline cursor-pointer"
          >
            Review how offshore status works on our Tax Optimization page
          </Link>
        </div>
      </div>
    </section>
  );
}
